import type { Drawing, HitTestResult } from './types';

const HIT_THRESHOLD = 8;

type Projectors = {
  timeToX: (tSec: number) => number | null;
  priceToY: (price: number) => number | null;
  width: number;
};

function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
  const qx = x1 + t * dx;
  const qy = y1 + t * dy;
  return Math.hypot(px - qx, py - qy);
}

function near(px: number, py: number, x: number, y: number, r = HIT_THRESHOLD): boolean {
  return Math.hypot(px - x, py - y) <= r;
}

function rayEndX(x1: number, y1: number, x2: number, y2: number, width: number): { x: number; y: number } {
  if (Math.abs(x2 - x1) < 1e-6) return { x: x2, y: y2 < y1 ? -1e6 : 1e6 };
  const m = (y2 - y1) / (x2 - x1);
  return { x: width, y: y1 + m * (width - x1) };
}

export function hitTestDrawings(
  drawings: Drawing[],
  px: number,
  py: number,
  projectors: Projectors,
): HitTestResult {
  for (let i = drawings.length - 1; i >= 0; i--) {
    const drawing = drawings[i];

    if (drawing.tool === 'note') {
      continue;
    }

    if (drawing.tool === 'hline') {
      const y = projectors.priceToY(Number((drawing.anchors as any).p));
      if (y == null) continue;
      if (Math.abs(py - y) <= HIT_THRESHOLD) return { drawingId: drawing.id, anchor: 'body' };
      continue;
    }

    if (drawing.tool === 'vline') {
      const x = projectors.timeToX(Number((drawing.anchors as any).t));
      if (x == null) continue;
      if (Math.abs(px - x) <= HIT_THRESHOLD) {
        return { drawingId: drawing.id, anchor: near(px, py, x, 14) ? 'p1' : 'body' };
      }
      continue;
    }

    const x1 = projectors.timeToX(Number((drawing.anchors as any).t1));
    const y1 = projectors.priceToY(Number((drawing.anchors as any).p1));
    const x2 = projectors.timeToX(Number((drawing.anchors as any).t2));
    const y2 = projectors.priceToY(Number((drawing.anchors as any).p2));
    if (x1 == null || y1 == null || x2 == null || y2 == null) continue;

    if (near(px, py, x1, y1)) return { drawingId: drawing.id, anchor: 'p1' };
    if (near(px, py, x2, y2)) return { drawingId: drawing.id, anchor: 'p2' };

    if (drawing.tool === 'rect') {
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      const inside = px >= left - HIT_THRESHOLD && px <= right + HIT_THRESHOLD && py >= top - HIT_THRESHOLD && py <= bottom + HIT_THRESHOLD;
      if (inside) return { drawingId: drawing.id, anchor: 'body' };
      continue;
    }

    if (drawing.tool === 'trend') {
      if (distToSegment(px, py, x1, y1, x2, y2) <= HIT_THRESHOLD) return { drawingId: drawing.id, anchor: 'body' };
      continue;
    }

    if (drawing.tool === 'ray') {
      const end = rayEndX(x1, y1, x2, y2, projectors.width);
      if (distToSegment(px, py, x1, y1, end.x, end.y) <= HIT_THRESHOLD) return { drawingId: drawing.id, anchor: 'body' };
      continue;
    }

    if (drawing.tool === 'channel') {
      const x3 = projectors.timeToX(Number((drawing.anchors as any).t3));
      const y3 = projectors.priceToY(Number((drawing.anchors as any).p3));
      if (x3 == null || y3 == null) continue;
      if (near(px, py, x3, y3)) return { drawingId: drawing.id, anchor: 'p3' };

      const vx = x2 - x1;
      const vy = y2 - y1;
      const len = Math.hypot(vx, vy) || 1;
      const nx = -vy / len;
      const ny = vx / len;
      const wx = x3 - x1;
      const wy = y3 - y1;
      const d = wx * nx + wy * ny;
      const ox = nx * d;
      const oy = ny * d;

      const hitBase = distToSegment(px, py, x1, y1, x2, y2) <= HIT_THRESHOLD;
      const hitParallel = distToSegment(px, py, x1 + ox, y1 + oy, x2 + ox, y2 + oy) <= HIT_THRESHOLD;
      if (hitBase || hitParallel) return { drawingId: drawing.id, anchor: 'body' };
    }
  }

  return { drawingId: null, anchor: 'none' };
}
